import { useEffect, useState } from 'react';
import { ICustomCheckoutPricing, IUseCustomCheckoutPricing } from '../../interfaces';
import { fetchPricingPreview } from '../services/registerApi';
import IPricingPreview from '../../interfaces/IPricingPreview';
import { usePricesContext } from '../ContextProviders/PricesContext';
import { getPriceByPlanCode } from '../Components/OrderSummary/utils/planPrices';
import { getExchangeRate } from '../services/localization';
import countryCodes from '../utils/countryCodes';
import formatPrice from '../utils/formatPrice';
import { getTaxRate } from '../services';
import { TaxResponse } from '../services/tax';

const EXCHANGE_RATE_INITIAL_VALUE = 0;
const PRICING_PREVIEW_INITIAL_VALUE = {
  total_charge_amount: 0,
  tax_amount: 0,
  shipping_fee_amount: 0,
  discount: 0,
  gift_card: 0,
  price_in_gbp: 0,
};

type StandardAPIResponse = Record<string, never>;

const useCustomCheckoutPricing = (
  customPricingObject: IUseCustomCheckoutPricing
): ICustomCheckoutPricing => {
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isLocalizationLoading, setIsLocalizationLoading] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(EXCHANGE_RATE_INITIAL_VALUE);
  const [pricingPreviewData, setPricingPreviewData] = useState<
    IPricingPreview | StandardAPIResponse
  >(PRICING_PREVIEW_INITIAL_VALUE);
  const [error, setError] = useState<string | null>(null);
  const [taxRate, setTaxRate] = useState<string>('0');
  const { prices, loading: isPricesLoading, setTotal } = usePricesContext();
  const planPrice = getPriceByPlanCode(prices, customPricingObject.planCode);
  const {
    shipping: { country, zipCode },
    coupon,
    planCode,
  } = customPricingObject;

  useEffect(() => {
    if (country && zipCode) {
      setIsPreviewLoading(true);

      const pricingPromise = fetchPricingPreview(customPricingObject);
      const taxRatePromise: Promise<TaxResponse | void> = getTaxRate(country, zipCode, planCode);

      Promise.all([pricingPromise, taxRatePromise])
        .then(([pricingResponse, taxRateResponse]) => {
          setPricingPreviewData(pricingResponse);
          if (taxRateResponse?.errors && taxRateResponse?.errors?.length > 0) {
            setTaxRate('0');
            setError(taxRateResponse?.errors[0]?.msg);
          } else {
            setTaxRate(taxRateResponse?.tax_rate ?? '0');
            setError(null);
          }
        })
        .catch((response) => {
          setPricingPreviewData(PRICING_PREVIEW_INITIAL_VALUE);
          setTaxRate('0');
          if ('errors' in response) {
            setError(response.errors[0]?.msg);
          }
        })
        .finally(() => setIsPreviewLoading(false));
    }
  }, [coupon, country, zipCode]);

  useEffect(() => {
    if (country === countryCodes.GB) {
      setIsLocalizationLoading(true);
      getExchangeRate()
        .then((response) => {
          setExchangeRate(response);
        })
        .catch(() => {
          setExchangeRate(EXCHANGE_RATE_INITIAL_VALUE);
        })
        .finally(() => {
          setIsLocalizationLoading(false);
        });
    }
  }, [country]);

  const total = formatPrice(pricingPreviewData?.total_charge_amount);
  const taxes = formatPrice(pricingPreviewData?.tax_amount);
  const shipping = formatPrice(pricingPreviewData?.shipping_fee_amount);
  const discount = formatPrice(pricingPreviewData?.discount);
  const giftCard = formatPrice(pricingPreviewData?.gift_card);
  const priceInGBP = total * exchangeRate;

  // Momentary logic meanwhile backend fix /preview endpoint returning wrong taxes
  // We are using Cart data to calculate taxes and total
  // We must remove after their changes
  if (customPricingObject.subtotal) {
    const { subtotal } = customPricingObject;
    // Renaming shipping_fee_amount to shippingFee, to avoid linter issues
    const { shipping_fee_amount: shippingFee } = pricingPreviewData;
    const totalWithoutTax =
      formatPrice(Number(shippingFee)) + formatPrice(Number(subtotal));
    const totalTaxes = Number(taxRate) * Number(totalWithoutTax);
    const realTotal = totalTaxes + totalWithoutTax;

    setTotal(realTotal.toString());
    return {
      total: realTotal,
      taxes: totalTaxes,
      shipping: formatPrice(Number(shippingFee)),
      plan: planPrice,
      discount,
      giftCard,
      priceInGBP,
      ...(error && { error }),
      isLoading: isPricesLoading || isPreviewLoading || isLocalizationLoading,
    };
  }

  setTotal(total.toFixed(2));
  return {
    total,
    taxes,
    shipping,
    plan: planPrice,
    discount,
    giftCard,
    priceInGBP,
    ...(error && { error }),
    isLoading: isPricesLoading || isPreviewLoading || isLocalizationLoading,
  };
};

export default useCustomCheckoutPricing;
